import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, proofUrl, WEB, type BookingFile, type Person } from "../api.ts";
import { day, digits, METHOD, money, PAY_STATUS, POLICY, range, STATUS, wa } from "../format.ts";
import { Act } from "../ui.tsx";

function Contact({ title, p, text }: { title: string; p: Person | null; text: string }) {
  if (!p) return <div className="card"><span className="eyebrow">{title}</span><p className="muted">Bien Sekani — aucun propriétaire tiers.</p></div>;
  return (
    <div className="card">
      <span className="eyebrow">{title}</span>
      <b className="big">{p.name}</b>
      <span className="num">{p.phone ? `+${digits(p.phone)}` : "pas de téléphone"}</span>
      {p.email && <span className="muted small">{p.email}</span>}
      <div className="quick">
        {p.phone && <a className="chip" href={`tel:+${digits(p.phone)}`}>Appeler</a>}
        {p.phone && <a className="chip" href={wa(p.phone, text) ?? "#"} target="_blank" rel="noreferrer">WhatsApp</a>}
      </div>
    </div>
  );
}

export function FilePage({ onChange }: { onChange: () => void }) {
  const { id = "" } = useParams();
  const [f, setF] = useState<BookingFile | null>(null);
  const [failed, setFailed] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { setF(await api<BookingFile>(`/v1/staff/bookings/${id}`)); } catch { setFailed(true); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { api<{ accessToken: string }>(`/v1/staff/bookings/${id}/guest-link`).then((r) => setLink(`${WEB}/b/${id}#${r.accessToken}`)).catch(() => setLink(null)); }, [id]);

  const act = (path: string, body?: unknown) => async () => { await api(`/v1/staff/${path}`, { method: "POST", body: body ?? {} }); await load(); onChange(); };

  if (failed) return <p className="err">Réservation introuvable.</p>;
  if (!f) return <div className="skel" />;

  const b = f.booking;
  const title = f.listing.title.fr;
  const stay = `« ${title} » du ${day(b.checkIn)} au ${day(b.checkOut)}`;
  const submitted = f.payments.find((p) => p.kind !== "refund" && p.status === "submitted");
  const due = f.payments.find((p) => p.kind === "deposit" && p.status === "due");
  const hostText = `Bonjour ${f.host?.name ?? ""}, ici Sekani. Un client souhaite séjourner à ${stay} (${b.nights} nuits, ${b.guests} pers.). Le bien est-il libre ? Merci de nous confirmer.`;
  const guestText =
    b.status === "requested" ? `Bonjour ${f.guest?.name ?? ""}, ici Sekani à propos de votre demande ${b.code}. Nous vérifions la disponibilité et revenons vers vous très vite.`
    : b.status === "awaiting_payment" ? `Bonjour ${f.guest?.name ?? ""}, bonne nouvelle : ${stay} est disponible. Montant à régler : ${money(b.totalMinor, b.currency)} (réf. ${b.code}). Suivez et réglez ici : ${link ?? ""}`
    : `Bonjour ${f.guest?.name ?? ""}, ici Sekani à propos de votre réservation ${b.code}.`;
  const open = ["requested", "awaiting_payment", "confirmed"].includes(b.status);

  return (
    <section>
      <Link to="/" className="back eyebrow">← Retour</Link>
      <header className="pagehead">
        <span className="eyebrow num">{b.code} · {f.listing.cityKey}</span>
        <h1>{title}</h1>
        <p><span className="pill">{STATUS[b.status] ?? b.status}</span> <span className="muted num">{range(b.checkIn, b.checkOut)} · {b.nights} nuits · {b.guests} pers.</span></p>
      </header>

      <div className="grid2">
        <div className="stackc">
          <div className="two">
            <Contact title="Client" p={f.guest} text={guestText} />
            <Contact title="Propriétaire" p={f.host} text={hostText} />
          </div>
          {b.guestMessage && <div className="card"><span className="eyebrow">Mot du client</span><p>{b.guestMessage}</p></div>}
          <div className="card">
            <span className="eyebrow">Bien</span>
            <p>{f.listing.address}</p>
            <p className="muted small num">Arrivée {f.listing.checkInTime} · Départ {f.listing.checkOutTime} · Annulation : {POLICY[b.cancellationPolicy] ?? b.cancellationPolicy}</p>
          </div>
          <div className="card">
            <span className="eyebrow">Montants</span>
            <div className="kv"><span>Total client</span><b className="num">{money(b.totalMinor, b.currency)}</b></div>
            <div className="kv"><span>Commission Sekani</span><span className="num">{money(b.hostCommissionMinor + b.guestFeeMinor, b.currency)}</span></div>
            <div className="kv"><span>{f.host ? "Dû au propriétaire" : "Bien Sekani"}</span><span className="num">{f.host ? money(b.totalMinor - b.hostCommissionMinor - b.guestFeeMinor, b.currency) : "—"}</span></div>
          </div>
          <div className="card">
            <span className="eyebrow">Paiements</span>
            {f.payments.length === 0 && <p className="muted small">Aucun paiement demandé pour l'instant.</p>}
            {f.payments.map((p) => (
              <div className="kv" key={p.id}>
                <span>{p.kind === "refund" ? "Remboursement" : "Paiement"} · <span className="pill sm">{PAY_STATUS[p.status] ?? p.status}</span> {p.method && <span className="muted small">{METHOD[p.method] ?? p.method}{p.reference ? ` · réf. ${p.reference}` : ""}</span>} {p.proofKey && <a className="chip" href={proofUrl(p.proofKey)} target="_blank" rel="noreferrer">Capture</a>}</span>
                <b className="num">{money(p.amountMinor, p.currency)}</b>
              </div>
            ))}
          </div>
          <details className="card">
            <summary className="eyebrow">Historique</summary>
            {f.history.map((h) => <div className="kv small" key={h.id}><span>{h.action}</span><span className="muted num">{new Date(h.createdAt).toLocaleString("fr-FR")}</span></div>)}
          </details>
        </div>

        <aside className="actions">
          <span className="eyebrow">Actions</span>
          {b.status === "requested" && (
            <>
              <p className="muted small">1. Appelez le propriétaire (ou consultez son calendrier). 2. Selon sa réponse :</p>
              <Act run={act(`bookings/${id}/confirm-availability`)}>Disponible — inviter à payer</Act>
              <input placeholder="Motif du refus" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Act tone="ghost" disabled={reason.trim().length < 3} run={act(`bookings/${id}/decline`, { reason })}>Indisponible — refuser</Act>
            </>
          )}
          {b.status === "awaiting_payment" && (
            <>
              <p className="muted small">Envoyez le lien de paiement au client (bouton WhatsApp ci-contre), puis vérifiez le compte.</p>
              {submitted ? (
                <>
                  <p className="small">Preuve reçue : <b>{METHOD[submitted.method ?? ""] ?? submitted.method}</b> {submitted.reference ? `· réf. ${submitted.reference}` : ""} — <b className="num">{money(submitted.amountMinor, submitted.currency)}</b></p>
                  {submitted.proofKey && <a className="chip" href={proofUrl(submitted.proofKey)} target="_blank" rel="noreferrer">Voir la capture d'écran</a>}
                  <Act run={act(`payments/${submitted.id}/verify`)}>Argent reçu — confirmer</Act>
                  <input placeholder="Motif du rejet" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Act tone="danger" disabled={reason.trim().length < 3} run={act(`payments/${submitted.id}/reject`, { reason })}>Rejeter la preuve</Act>
                </>
              ) : due ? (
                <Act tone="ghost" run={act(`payments/${due.id}/record-cash`)}>Espèces reçues en main propre</Act>
              ) : null}
            </>
          )}
          {b.status === "confirmed" && (
            <>
              <p className="muted small">Récupérez les clés auprès du propriétaire, puis remettez-les au client.</p>
              <Act run={act(`bookings/${id}/hand-over-keys`)}>Clés remises — séjour commencé</Act>
            </>
          )}
          {b.status === "in_stay" && <p className="muted small">Séjour en cours. La clôture et le versement au propriétaire sont automatiques le lendemain du départ.</p>}
          {!open && b.status !== "in_stay" && <p className="muted small">Dossier clos{b.cancelReason ? ` — ${b.cancelReason}` : ""}.</p>}

          {open && (
            <details className="cancel">
              <summary className="small">Annuler ce séjour…</summary>
              <input placeholder="Motif" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Act tone="danger" run={act(`bookings/${id}/cancel`, { initiatedBy: "guest", reason: reason || undefined })}>Annulé par le client</Act>
              <Act tone="danger" disabled={reason.trim().length < 3} run={act(`bookings/${id}/cancel`, { initiatedBy: "host", reason })}>Annulé par le propriétaire (remboursement intégral)</Act>
            </details>
          )}
        </aside>
      </div>
    </section>
  );
}
